/**
 * AppFooter - the installation legal-footer bar.
 *
 * The one component that renders the operator's legal footer wherever it is
 * mounted: both tenant-app layouts, the workspace shell, the shared LoginPage
 * footer slot, and the System Settings live preview inside the Legal footer
 * card (SPEC-PLATFORM-BRANDING RULE-006b, UI-003). It is a PURE FUNCTION OF ITS
 * PROPS - it fetches nothing, reads no token, writes no storage and renders
 * identically for every viewer. A consumer that wants it to fetch changes the
 * BrandingProvider, not this component.
 *
 * WHAT IT RENDERS, PER `state`:
 * - `hidden`   - nothing at all, not even an empty element. Same for a null
 *   `values`, a `values.enabled` of false, and a values object whose every
 *   field resolves empty (SCENARIO-002: nothing in the accessibility tree).
 * - `unavailable` - EXACTLY ONE translated line, "Legal notices are temporarily
 *   unavailable", in a polite `role="status"` live region, and NO link row. The
 *   legal footer fails VISIBLE (RULE-001): a footer that is absent because the
 *   read failed is a violation, which is why this state is a rendered line and
 *   not an early return.
 * - `ok` - the bar, as TWO GROUPS on one wrapping row (D-BR-27): the line at
 *   the start edge, then the link row and the imprint disclosure together at
 *   the end edge. The revealed imprint block stays a full-width sibling BELOW
 *   that row, under both groups.
 *
 * LANGUAGE RESOLUTION LIVES HERE, ONCE (RULE-002). `locale` is taken as the RAW
 * UI locale and resolved inside this component: a locale whose primary subtag is
 * `hu` selects the `hu` value, ANY other locale selects the `en` value, and an
 * empty `en` value falls back to `hu`. A field that resolves empty is not
 * rendered at all - an empty link is no anchor, an empty block is no button.
 * Hungarian and English are the only content languages, which is RULE-002's
 * recorded divergence from SPEC-FRONTEND-RULE-006's seven-locale sentence; the
 * four mount sites therefore pass `useLocale()`'s value straight through and no
 * call site can drift.
 *
 * SECURITY.
 * - ONE SINK. The component contains exactly ONE `dangerouslySetInnerHTML`, in
 *   {@link SanitisedHtmlBlock}, fed only by `sanitizeHtmlSubset`. Both HTML
 *   fields (the line and the imprint block) route through that single sink
 *   rather than each opening one of their own.
 * - HREF IS VALIDATED AT RENDER (RULE-015). An anchor is rendered only for a
 *   value that parses as an http(s) URL; a `javascript:`, `data:` or relative
 *   value renders the label as plain text. This is enforced INDEPENDENTLY of the
 *   gateway check because the live preview renders UNSAVED draft values no
 *   server has seen (SCENARIO-016), and because the login page renders before a
 *   principal exists. React does not sanitise `href`.
 *
 * THE BAR IS `<Box component="footer">`, NOT A RAW `<footer>`. It renders the
 * real `<footer>` element while keeping every visual value in `sx` tokens - a
 * raw `<footer>` can only be styled through `style={{...}}` literals, which is
 * the inline-hex/magic-px defect RULE-034 bans. The ESLint `RAW_HTML_SELECTORS`
 * entry added with this component therefore needs no suppression here: it stays
 * a pure guard pointing future authors of a raw `<footer>` at this component.
 *
 * WHERE THE BAR SITS (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29). On every SHELL
 * of RULE-006a it is the LAST CHILD of the shell's full-height column, spanning
 * the full width UNDER both the navigation and the scrolling content, and it is
 * NOT inside the region that scrolls. It supersedes D-BR-20's "sticky inside the
 * scrolling content column" and D-BR-26's "beside whatever navigation that
 * surface has"; both are history. The LoginPage slots and the System Settings
 * live preview are not shells and are unchanged.
 *
 * THE `contentinfo` ROLE IS WRITTEN OUT, AND THAT IS NOT REDUNDANT. Per HTML-AAM
 * a `<footer>` maps to `contentinfo` ONLY while it is not a descendant of
 * `article`, `aside`, `main`, `nav` or `section`; inside one it maps to
 * `generic`. The bar is outside `<main>` on every shell since RULE-020, so the
 * role is no longer strictly required there - but it was required for a whole
 * ruling's lifetime, it costs nothing, and the live preview inside the System
 * Settings page still renders this element inside that page's `<main>`, where
 * dropping it WOULD lose the landmark. It stays, and it stays asserted.
 * `@testing-library/dom`'s implicit role table reports `contentinfo` regardless
 * of ancestry, which is why the original gap survived a green suite and was
 * found by reading HTML-AAM rather than by a red test.
 *
 * NOT USED HERE: `MarkdownPanel`. It cannot fit a 32 px bar, and its
 * "never re-sanitise on the client" contract conflicts with D-BR-07, which
 * requires exactly that second pass. Its stale "the ONE legitimate
 * dangerouslySetInnerHTML" comment is corrected in STORY-PB-HYGIENE, not here.
 *
 * @see SPEC-PLATFORM-BRANDING-UI-003
 * @see SPEC-PLATFORM-BRANDING-RULE-002 / RULE-003 / RULE-006 / RULE-015
 * @see SPEC-FRONTEND-RULE-035 (the new-shared-component Definition of Done)
 * @see TASK-PB-APPFOOTER-COMPONENT
 */

import React, { useCallback, useId, useMemo, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box, Button, Link, Typography } from '@mui/material';
import { useLingui } from '@lingui/react/macro';
import { colors } from '../../../theme/colors';
import { sanitizeHtmlSubset } from '../../../utils/htmlSubset';
import { SURFACE_RADIUS } from '../PageShell/tokens';

/**
 * A `{hu, en}` content map. Both halves are optional so the same type describes
 * the API response AND a half-typed draft in the live preview.
 */
export interface LocalisedValue {
  /** The Hungarian value. Also the fallback for every non-Hungarian locale. */
  hu?: string;
  /** The English value. Used for every locale that is not Hungarian. */
  en?: string;
}

/** The three fixed legal links. Each is a plain URL string, never HTML. */
export interface LegalFooterLinks {
  /** Privacy notice / Adatkezelesi tajekoztato. */
  privacy?: LocalisedValue;
  /** Terms / ASZF. */
  terms?: LocalisedValue;
  /** Accessibility statement / Akadalymentesitesi nyilatkozat. */
  accessibility?: LocalisedValue;
}

/**
 * The `legal_footer` object of `GET /api/v1/branding`
 * (SPEC-PLATFORM-BRANDING CONTRACT-001), or the draft of it being edited in the
 * Legal footer card.
 */
export interface LegalFooterValues {
  /** Operator switch. `false` renders nothing at all (RULE-006b). */
  enabled: boolean;
  /** The footer line. Operator-authored HTML in the RULE-023 subset. */
  line?: LocalisedValue;
  /** The imprint block revealed by the disclosure. HTML in the same subset. */
  impressum?: LocalisedValue;
  /** The three fixed legal links. */
  links?: LegalFooterLinks;
}

/**
 * The provider's read state, passed through with no translation:
 * `ok` the record was read, `unavailable` the read failed (RULE-001),
 * `hidden` this surface does not show a footer.
 */
export type AppFooterState = 'ok' | 'unavailable' | 'hidden';

/** Props of {@link AppFooter}. */
export interface AppFooterProps {
  /** The legal-footer record, or `null` when there is none to render. */
  values: LegalFooterValues | null;
  /** Which of the three renderings applies. */
  state: AppFooterState;
  /** The RAW UI locale (e.g. `hu`, `en`, `hu-HU`). Resolved per RULE-002 here. */
  locale: string;
}

/**
 * The bar's MINIMUM height in CSS px (RULE-006b). It is a FLOOR, never a fixed
 * height: the Hungarian labels run roughly 60 percent longer than the English
 * ones, so the row wraps and the bar grows (SPEC-FRONTEND-RULE-047).
 */
const APP_FOOTER_MIN_HEIGHT = 32;

/**
 * The WCAG 2.1 AA pointer-target floor in CSS px, applied to the disclosure
 * button (SPEC-FRONTEND-RULE-046 / RULE-007).
 */
const MIN_TARGET_SIZE = 24;

/**
 * Focus-ring width and offset in CSS px. The theme carries no focus-ring token
 * yet (`theme/themes.ts` defines none), and RULE-035 clause c requires a VISIBLE
 * focus indicator, so the two values are named here rather than typed inline.
 */
const FOCUS_RING_WIDTH = 2;

/**
 * THE BOUND ON THE REVEALED IMPRINT BLOCK (SPEC-PLATFORM-BRANDING RULE-020,
 * D-BR-29). The imprint expands INLINE inside the bar - never a dialog
 * (RULE-006b) - and the operator may publish up to 4000 characters. Now that the
 * bar is the shell's full-width BOTTOM ROW, an opened imprint of that size would
 * take most of the viewport from the application, which the rule names as a
 * failure. So the block is bounded and SCROLLS INSIDE ITSELF.
 *
 * IT IS A SCROLL CONTAINER, NEVER A TRUNCATION: every character stays reachable,
 * nothing is clipped away and no ellipsis is rendered (`AppFooter.test.tsx`
 * asserts the LAST words of a long imprint are still in the block).
 *
 * VIEWPORT-RELATIVE rather than a px constant, because what is being bounded is
 * a SHARE OF THE VIEWPORT: 200 px is a third of a phone's screen and a tenth of
 * a desktop's, and the failure semantics are about the application being pushed
 * out of view, not about any absolute height.
 */
const IMPRINT_MAX_HEIGHT = '30vh';

/** `hu` and `hu-HU` are Hungarian; everything else resolves through `en`. */
const isHungarianLocale = (locale: string): boolean =>
  (locale || '').toLowerCase().split(/[-_]/)[0] === 'hu';

/**
 * RULE-002 resolution for one field: the locale's value when non-empty, else the
 * Hungarian value, else the empty string (which means "do not render").
 */
const resolveLocalised = (
  value: LocalisedValue | undefined,
  hungarian: boolean,
): string => {
  if (!value) return '';
  const preferred = (hungarian ? value.hu : value.en) ?? '';
  if (preferred.trim()) return preferred.trim();
  return (value.hu ?? '').trim();
};

/**
 * RULE-015 render-side check: an anchor is rendered only for an absolute
 * http(s) URL. A relative path throws in the URL parser and is refused with it.
 */
const isRenderableHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * THE ONE SANCTIONED `dangerouslySetInnerHTML` SINK OF THIS COMPONENT.
 *
 * Both HTML fields render through this single component rather than opening a
 * sink each, so "AppFooter contains exactly one sink" is a property of the file
 * and not a promise. The input is ALWAYS the output of `sanitizeHtmlSubset`.
 *
 * @see SPEC-PLATFORM-BRANDING-RULE-003
 */
const SanitisedHtmlBlock: React.FC<{
  /** Already sanitised HTML - never a raw operator string. */
  html: string;
  /** Optional element id, so a disclosure button's `aria-controls` resolves. */
  id?: string;
  /** Hides the block while keeping it in the DOM for `aria-controls`. */
  hidden?: boolean;
  /** Test hook. */
  testId?: string;
  /** Token-only styling from the caller. */
  sx?: SxProps<Theme>;
}> = ({ html, id, hidden, testId, sx }) => (
  <Box
    id={id}
    hidden={hidden}
    data-testid={testId}
    sx={sx}
    // eslint-disable-next-line react/no-danger -- component-ok: SPEC-PLATFORM-BRANDING RULE-003 sanctions this sink; the value is server-sanitised and re-purified here
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

/**
 * The bar itself. A floor height, never a fixed one; it wraps and grows.
 *
 * THE STICKY DECLARATION IS NOW FOR THE NON-SHELL SURFACES ONLY, AND IT STAYS
 * FOR THEM (RULE-020, D-BR-29). On every SHELL of RULE-006a the bar is the LAST
 * CHILD of the shell's full-height column and the column does not scroll, so
 * "always visible" (D-BR-04) is STRUCTURAL there and `sticky` has nothing to
 * stick to - it is inert, not load-bearing. The three surfaces that are NOT
 * shells still scroll the bar inside a column and still need it:
 * the tenant and workspace LoginPage footer slots, and the System Settings live
 * preview. Deleting the declaration would change those three, so it stays,
 * described for what it now is rather than for what it was.
 *
 * `fixed` IS STILL REFUSED, by both rulings: a fixed bar leaves the column,
 * covers whatever is behind it and follows the viewport rather than the layout.
 *
 * `backgroundColor` IS LOAD-BEARING, not decoration: on the surfaces above,
 * content scrolls UNDERNEATH the bar, so a transparent one would render the
 * operator's legal notice over whatever happens to be behind it. It is the same
 * opaque `colors.background.secondary` token the bar already carried (an opaque
 * value in `theme/colors.ts`), named here so a later "tidy" cannot drop it as
 * cosmetic - and RULE-018 fixes it to the PAGE colour, never the brand colour.
 *
 * `zIndex` is the theme's `appBar` layer (1100), deliberately BELOW `drawer`
 * (1200) and `modal` (1300): a dialog is a portal above the bar, so it still
 * covers it, which is the overlap answer D-BR-20 records and RULE-020 keeps.
 */
const barSx: SxProps<Theme> = {
  minHeight: APP_FOOTER_MIN_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 0.5,
  px: 2,
  py: 0.75,
  position: 'sticky',
  bottom: 0,
  zIndex: (theme: Theme) => theme.zIndex.appBar,
  borderTop: '1px solid',
  borderColor: colors.border.default,
  backgroundColor: colors.background.secondary,
  color: colors.text.secondary,
  typography: 'caption',
};

/** The line + links + disclosure row. Wraps rather than clipping (RULE-047). */
const rowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 0.5,
};

/**
 * THE LEGAL GROUP AT THE END EDGE (D-BR-27, RULE-006b as amended 2026-09-21).
 *
 * The owner ruled the bar should read like the bottom band of the verisa.ai
 * marketing footer: the operator's company/copyright text on one side, the legal
 * links on the other. The line is the row's first child and stays where it is;
 * the three links AND the imprint disclosure move into this one group, which is
 * pushed to the end edge.
 *
 * `marginLeft: 'auto'` IS THE MECHANISM, AND `justifyContent: 'space-between'`
 * ON THE ROW IS NOT. A record may carry links with NO line (the line is
 * optional, and the live preview hands in half-typed drafts): with
 * space-between the group would then be the row's only child and would sit at
 * the START edge - the ruling inverted, silently, on exactly the record nobody
 * screenshots. An auto start-margin puts the group at the end edge whether or
 * not the line is there.
 *
 * It is a wrapping flex row of its OWN, carrying the same gaps as the outer row,
 * so the Hungarian labels keep wrapping and growing the bar rather than clipping
 * (SPEC-FRONTEND-RULE-047) now that they no longer wrap against the outer row
 * directly.
 */
const legalGroupSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 0.5,
  marginLeft: 'auto',
};

/** The sanitised line. Its paragraphs must not add the browser's own margins. */
const lineSx: SxProps<Theme> = {
  '& p': { m: 0 },
  '& ul, & ol': { m: 0, pl: 2 },
  '& a': { color: colors.text.clickable },
  '& a:hover': { color: colors.text.clickableHover },
};

/**
 * The revealed imprint block: BOUNDED AND SCROLLING, never truncated
 * (RULE-020 - see {@link IMPRINT_MAX_HEIGHT} for the reason and the limit).
 * `overflowY: 'auto'` is the whole mechanism; there is deliberately no
 * `textOverflow`, no `WebkitLineClamp` and no clipping of any kind, so the
 * bound costs the reader a scroll and never a character.
 */
const imprintSx: SxProps<Theme> = {
  width: '100%',
  maxHeight: IMPRINT_MAX_HEIGHT,
  overflowY: 'auto',
  mt: 0.5,
  px: 1,
  py: 1,
  borderRadius: SURFACE_RADIUS,
  backgroundColor: colors.background.tertiary,
  color: colors.text.secondary,
  '&[hidden]': { display: 'none' },
  '& p': { m: 0 },
  '& ul, & ol': { m: 0, pl: 2 },
  '& a': { color: colors.text.clickable },
};

/** Shared by the anchors and the un-anchored labels, so the row reads as one. */
const labelSx: SxProps<Theme> = {
  color: colors.text.clickable,
  textDecoration: 'none',
};

/** The disclosure button: a real button, tokens only, at the target floor. */
const disclosureSx: SxProps<Theme> = {
  minHeight: MIN_TARGET_SIZE,
  minWidth: MIN_TARGET_SIZE,
  p: 0,
  px: 0.5,
  typography: 'caption',
  textTransform: 'none',
  color: colors.text.clickable,
  '&:hover': {
    color: colors.text.clickableHover,
    backgroundColor: colors.background.hover,
  },
  '&:focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: FOCUS_RING_WIDTH,
    outlineColor: colors.interactive.active,
    outlineOffset: FOCUS_RING_WIDTH,
  },
};

/**
 * The installation legal-footer bar.
 *
 * @param props - See {@link AppFooterProps}.
 */
const AppFooter: React.FC<AppFooterProps> = ({ values, state, locale }) => {
  const { t } = useLingui();
  const imprintId = useId();
  const [imprintOpen, setImprintOpen] = useState(false);

  const hungarian = isHungarianLocale(locale);

  const line = resolveLocalised(values?.line, hungarian);
  const impressum = resolveLocalised(values?.impressum, hungarian);

  // Memoised on the RESOLVED string, so a parent re-render never re-purifies
  // unchanged HTML (the PERFORMANCE criterion of this TASK).
  const lineHtml = useMemo(() => sanitizeHtmlSubset(line), [line]);
  const impressumHtml = useMemo(() => sanitizeHtmlSubset(impressum), [impressum]);

  const toggleImprint = useCallback(() => setImprintOpen((open) => !open), []);

  if (state === 'hidden') return null;

  if (state === 'unavailable') {
    return (
      // `role` is explicit, not redundant: inside a <main> a bare <footer> maps
      // to `generic` per HTML-AAM, which is still the live preview's case (see
      // the header block).
      <Box component="footer" role="contentinfo" sx={barSx} data-testid="app-footer">
        <Typography role="status" variant="caption" data-testid="app-footer-unavailable">
          {t`Legal notices are temporarily unavailable`}
        </Typography>
      </Box>
    );
  }

  // state === 'ok' from here on.
  if (!values || values.enabled === false) return null;

  const links = [
    { key: 'privacy', label: t`Privacy notice`, url: resolveLocalised(values.links?.privacy, hungarian) },
    { key: 'terms', label: t`Terms`, url: resolveLocalised(values.links?.terms, hungarian) },
    {
      key: 'accessibility',
      label: t`Accessibility statement`,
      url: resolveLocalised(values.links?.accessibility, hungarian),
    },
  ].filter((link) => link.url !== '');

  // Every field empty - including a line that sanitised down to nothing - is the
  // same as disabled: no element, nothing in the accessibility tree.
  if (!lineHtml && !impressumHtml && links.length === 0) return null;

  return (
    // Same explicit landmark as the unavailable branch above. It is no longer
    // load-bearing on the shells (RULE-020 puts the bar OUTSIDE <main>), but the
    // System Settings live preview still renders this element inside that page's
    // <main>, where HTML-AAM would otherwise give it no role at all.
    <Box component="footer" role="contentinfo" sx={barSx} data-testid="app-footer">
      <Box sx={rowSx}>
        {lineHtml ? (
          <SanitisedHtmlBlock html={lineHtml} testId="app-footer-line" sx={lineSx} />
        ) : null}

        {/* The legal group at the END edge (D-BR-27). Rendered only when it
            has something in it, so an empty element never reaches the
            accessibility tree of a line-only record. */}
        {links.length > 0 || impressumHtml ? (
          <Box sx={legalGroupSx} data-testid="app-footer-legal-group">
            {links.map((link) =>
              isRenderableHttpUrl(link.url) ? (
                <Link
                  key={link.key}
                  href={link.url}
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={labelSx}
                  data-testid={`app-footer-link-${link.key}`}
                >
                  {link.label}
                </Link>
              ) : (
                // RULE-015: a value the URL parser refuses keeps its LABEL and
                // loses its anchor - the reader still learns the notice exists.
                <Typography
                  key={link.key}
                  component="span"
                  variant="caption"
                  sx={labelSx}
                  data-testid={`app-footer-link-${link.key}`}
                >
                  {link.label}
                </Typography>
              ),
            )}

            {impressumHtml ? (
              <Button
                type="button"
                size="small"
                disableRipple
                onClick={toggleImprint}
                aria-expanded={imprintOpen}
                aria-controls={imprintId}
                sx={disclosureSx}
                data-testid="app-footer-imprint-toggle"
              >
                {t`Imprint`}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {impressumHtml ? (
        <SanitisedHtmlBlock
          id={imprintId}
          hidden={!imprintOpen}
          html={impressumHtml}
          testId="app-footer-imprint"
          sx={imprintSx}
        />
      ) : null}
    </Box>
  );
};

export default AppFooter;
